<template>
  <header class="sticky top-0 z-50 w-full bg-white shadow-sm box-border">
    <div
      class="flex h-16 items-center justify-between gap-2 px-4 md:px-6 lg:px-8 min-w-0"
    >
      <div class="flex flex-1 items-center gap-2 min-w-0 md:gap-3">
        <button
          type="button"
          class="lg:hidden inline-flex items-center justify-center rounded-md border border-gray-200 h-10 w-10 shrink-0 hover:bg-gray-100 transition-colors"
          aria-label="打开菜单"
          @click="toggleSidebar"
        >
          <i class="fas fa-bars text-gray-700"></i>
        </button>
        <span
          class="lg:hidden text-sm font-semibold text-gray-800 truncate min-w-0"
        >
          银企通™ API
        </span>
      </div>

      <div class="flex items-center gap-1 shrink-0 md:gap-2">
        <!-- 版本切换（仅 >=768px） -->
        <div class="relative header-dropdown hidden md:block">
          <button
            type="button"
            @click.stop="toggleVersionDropdown"
            class="header-btn"
          >
            <span class="truncate">{{ currentVersion?.label }}</span>
            <i class="fas fa-chevron-down ml-2 text-xs shrink-0"></i>
          </button>
          <div
            v-show="showVersionDropdown"
            class="absolute right-0 mt-1 w-48 bg-white border border-gray-200 rounded-md shadow-lg z-50"
          >
            <div
              v-for="version in versionList"
              :key="version.value"
              @click="switchVersion(version)"
              class="px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer"
              :class="{ 'bg-gray-100': version.value === currentVersion?.value }"
            >
              {{ version?.label || "默认版本" }}
            </div>
          </div>
        </div>

        <!-- >=768px：完整操作按钮 -->
        <div class="hidden md:flex items-center gap-2">
          <a
            v-if="!isLoggedIn"
            :href="systemStore.nextbackHome"
            target="_blank"
            class="header-btn"
          >
            银企通™官网
          </a>
          <a
            v-else
            :href="getBaseDomain()"
            target="_blank"
            class="header-btn"
          >
            返回主页面
          </a>

          <template v-if="!isLoggedIn">
            <button type="button" @click="getApiKeys" class="header-btn">
              获取 API Keys
            </button>
            <button
              type="button"
              @click="login"
              class="header-btn header-btn-primary"
            >
              登录
            </button>
          </template>
        </div>

        <!-- 已登录 -->
        <div v-if="isLoggedIn" class="flex items-center">
          <NavUser
            :version-list="versionList"
            :current-version="currentVersion"
            @switch-version="switchVersion"
          />
        </div>

        <!-- <768px 未登录：登录 + ⋯ 右侧成组 -->
        <div
          v-if="!isLoggedIn"
          class="flex md:hidden items-center gap-1 shrink-0"
        >
          <button
            type="button"
            @click="login"
            class="header-btn header-btn-primary px-3"
          >
            登录
          </button>
          <div class="relative header-dropdown">
            <button
              type="button"
              class="header-btn px-2.5"
              aria-label="更多操作"
              @click.stop="toggleMoreDropdown"
            >
              <i class="fas fa-ellipsis-h"></i>
            </button>
            <div
              v-show="showMoreDropdown"
              class="absolute right-0 mt-1 w-48 bg-white border border-gray-200 rounded-md shadow-lg z-50 py-1"
            >
              <div class="px-4 py-2 text-xs text-gray-500">文档版本</div>
              <button
                v-for="version in versionList"
                :key="version.value"
                type="button"
                class="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                :class="{
                  'bg-gray-100': version.value === currentVersion?.value,
                }"
                @click="switchVersion(version)"
              >
                {{ version?.label || "默认版本" }}
              </button>
              <div class="border-t border-gray-100 my-1"></div>
              <a
                :href="systemStore.nextbackHome"
                target="_blank"
                class="block px-4 py-2 text-sm hover:bg-gray-100"
                @click="showMoreDropdown = false"
              >
                银企通™官网
              </a>
              <button
                type="button"
                class="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                @click="
                  getApiKeys();
                  showMoreDropdown = false;
                "
              >
                获取 API Keys
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </header>
</template>

<script setup lang="ts">
import { computed, onMounted, onBeforeUnmount, ref } from "vue";
import { useUserStore } from "../stores/user";
import { useSystemStore } from "../stores/system";
import NavUser from "./NavUser.vue";
import { getBaseDomain } from "@/lib/config";
import { toggleSidebar } from "@/composables/useLayout";

interface VersionItem {
  label?: string;
  value?: string;
  url?: string;
}

const userStore = useUserStore();
const systemStore = useSystemStore();

const showVersionDropdown = ref(false);
const showMoreDropdown = ref(false);
const currentVersion = ref<VersionItem | null>(null);

const isLoggedIn = computed(() => userStore.logined);

const versionList = computed(() => {
  return (systemStore.systemConfig?.VERSION_CONFIG_LIST || []) as VersionItem[];
});
currentVersion.value = versionList.value[0] ?? null;

let loginStatusCheckTimer: number | null = null;

const checkLoginStatus = async () => {
  if (userStore.logined) {
    const isValid = await userStore.validateLoginStatus();
    if (!isValid) {
      window.dispatchEvent(new Event("login-status-changed"));
    }
  }
};

const handleLoginStatusChanged = () => {
  userStore.forceUpdateLoginStatus();
};

const startLoginStatusCheck = () => {
  if (loginStatusCheckTimer) {
    clearInterval(loginStatusCheckTimer);
    loginStatusCheckTimer = null;
  }

  if (userStore.logined) {
    loginStatusCheckTimer = window.setInterval(checkLoginStatus, 5 * 60 * 1000);
  }
};

const stopLoginStatusCheck = () => {
  if (loginStatusCheckTimer) {
    clearInterval(loginStatusCheckTimer);
    loginStatusCheckTimer = null;
  }
};

const toggleVersionDropdown = () => {
  showVersionDropdown.value = !showVersionDropdown.value;
  showMoreDropdown.value = false;
};

const toggleMoreDropdown = () => {
  showMoreDropdown.value = !showMoreDropdown.value;
  showVersionDropdown.value = false;
};

const isSameVersion = (
  version: VersionItem,
  active: VersionItem | null
): boolean => {
  if (!active) return false;
  if (version.value && active.value) {
    return version.value === active.value;
  }
  if (version.url && active.url) {
    return version.url === active.url;
  }
  return version.label === active.label;
};

const switchVersion = (version: VersionItem) => {
  showVersionDropdown.value = false;
  showMoreDropdown.value = false;
  if (isSameVersion(version, currentVersion.value)) {
    return;
  }
  currentVersion.value = version;
  if (version.url) {
    window.location.href = version.url;
  }
};

const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as HTMLElement;
  if (!target.closest(".header-dropdown")) {
    showVersionDropdown.value = false;
    showMoreDropdown.value = false;
  }
};

onMounted(() => {
  startLoginStatusCheck();
  window.addEventListener("login-status-changed", handleLoginStatusChanged);
  document.addEventListener("click", handleClickOutside);
});

onBeforeUnmount(() => {
  stopLoginStatusCheck();
  window.removeEventListener("login-status-changed", handleLoginStatusChanged);
  document.removeEventListener("click", handleClickOutside);
});

const login = () => {
  const currentUrl = window.location.href;
  sessionStorage.setItem("redirectUrl", currentUrl);

  const isDev = import.meta.env.DEV;
  const baseUrl =
    isDev && import.meta.env.VITE_AUTH_DOMAIN
      ? import.meta.env.VITE_AUTH_DOMAIN
      : window.location.origin;
  const loginPath = "/#/passport/login";
  window.location.href = `${baseUrl}${loginPath}?redirectUrl=${encodeURIComponent(
    currentUrl
  )}`;
};

const getApiKeys = () => {
  const isDev = import.meta.env.DEV;
  const baseUrl =
    isDev && import.meta.env.VITE_AUTH_DOMAIN
      ? import.meta.env.VITE_AUTH_DOMAIN
      : window.location.origin;
  const registerPath = "/#/passport/sign";
  window.open(baseUrl + registerPath, "_blank");
};
</script>

<style scoped>
.header-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 500;
  transition: color 0.15s, background-color 0.15s;
  height: 2.25rem;
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--border);
  white-space: nowrap;
}

@media (min-width: 768px) {
  .header-btn {
    padding: 0.5rem 1rem;
  }
}

.header-btn:hover {
  background-color: #f3f4f6;
}

.header-btn-primary {
  background-color: #623bee;
  color: white;
  border-color: #623bee;
}

.header-btn-primary:hover {
  background-color: #7a5aff;
  color: white;
}
</style>
